import type { PushPermission } from './pushPermission';

/*
 * When the "Turn on desktop alerts" banner shows (owner, 2026-10-24).
 *
 * Only while Chrome has no answer: once permission is granted or denied it never shows again,
 * because a granted console has nothing to ask and a denied one cannot ask (Chrome refuses a
 * second prompt; the alerts page explains how to unblock instead). "Not now" hides it for seven
 * days, per staff member and per browser, so a shared machine does not snooze it for a colleague.
 *
 * Storage can be refused (private window, cleared site data). Every read and write is guarded and
 * a refusal reads as "not snoozed", which at worst shows the banner once more.
 */

export const PUSH_PROMPT_SNOOZE_DAYS = 7;
const DAY_MS = 86_400_000;

export const pushPromptSnoozeKey = (userId: string) => `cc_push_prompt_snoozed_until:${userId}`;

type SnoozeStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function defaultStorage(): SnoozeStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function isPushPromptSnoozed(
  userId: string,
  now: number = Date.now(),
  storage: SnoozeStorage | null = defaultStorage(),
): boolean {
  try {
    const until = Number(storage?.getItem(pushPromptSnoozeKey(userId)));
    return Number.isFinite(until) && until > now;
  } catch {
    return false;
  }
}

export function snoozePushPrompt(
  userId: string,
  now: number = Date.now(),
  storage: SnoozeStorage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(pushPromptSnoozeKey(userId), String(now + PUSH_PROMPT_SNOOZE_DAYS * DAY_MS));
  } catch {
    // Storage refused: the banner comes back next load, which is the honest outcome.
  }
}

export function shouldShowPushPrompt(args: {
  userId?: string;
  permission: PushPermission;
  snoozed: boolean;
}): boolean {
  return Boolean(args.userId) && args.permission === 'default' && !args.snoozed;
}
